<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import type { ComputedRef } from 'vue'
import BsNormalCard from '@/legacy/components/NormalCard.vue'
import BsProductSharingLikeIcon from '@/legacy/components/ProductSharingLikeIcon.vue'
import { createGAEvent } from '@/assets/ts/customEvent'
import {
	ProductSharingImageSize,
	getTimeAgoValue,
	kFormatter,
	MemberTypes,
} from '@/legacy/ts/productSharing'
import {
	getformatFloatPrice,
	getCurrencySymbolOriginal,
} from '@/assets/ts/currency'
import { useBreakpoints, getVueAssetImagePath } from '@/lib/utils'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const props = defineProps({
	post: {
		type: Object,
		default: null,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	columnCount: {
		type: Number,
		default: 1,
	},
	text: {
		type: Object,
		default: null,
	},
	isSearch: {
		type: Boolean,
		default: false,
	},
	isExplore: {
		type: Boolean,
		default: false,
	},
	isIndexLogin: {
		type: Boolean,
		default: false,
	},
	localCurrencyInfo: {
		type: Array,
	},
	isCurrentModeDefault: {
		type: Boolean,
		default: false,
	},
	supportedCurrencies: {
		type: Object,
	},
	warehouseIdMap: {
		type: Object,
	},
	gridId: {
		type: String,
	},
	isBgOrange: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['like', 'view-post'])
const postItem = ref<HTMLElement>()
const likeIconCompoent = ref<InstanceType<
	typeof BsProductSharingLikeIcon
> | null>(null)
const paddingSpace = ref(6.5)
const currencySymbolOriginal = ref(null)
const { isSmall: isMobile } = useBreakpoints()

const postItemInfo: ComputedRef<any> = computed(() => {
	if (props.post === null) return null
	return {
		...props.post,
		postTimeDisplay: getTimeAgoValue(
			props.post.publishDate,
			props.text.timeago,
		),
	}
})

const postItemImageSrc = computed(() => {
	return getMedia(postItemInfo.value?.media[0])
})

const postSymbols = computed(() => {
	if (!postItemInfo.value?.media.length) return null
	return postItemInfo.value?.media[0].type === 'ps_video'
		? 'video'
		: postItemInfo.value?.media.length > 1
		  ? 'images'
		  : null
})

const calculateImageHeight = computed(() => {
	const target = props.gridId ? `#${props.gridId}` : '.mansory-grid'
	const $targetDiv = document.querySelector(target)
	const eleWidth = $targetDiv?.clientWidth ? $targetDiv.clientWidth : 200
	const targetWidth = eleWidth / props.columnCount - paddingSpace.value * 2
	return Math.round(targetWidth / parseFloat(postItemInfo.value.mediaRatio))
})

const totalLikeCountDisplay = computed(() => {
	if (postItemInfo.value.likeCount) {
		return kFormatter(postItemInfo.value.likeCount, props.text)
	}
	return 0
})

// const eventParams = computed(() => {
//   const defaultObj = { category: postItemInfo.value.slug || 'none', label: postItemInfo.value.id }
//   if (props.isSearch) return { ...defaultObj, value: 'search' }
//   if (props.isExplore) return { ...defaultObj, value: 'explore' }
//   if (props.isIndexLogin) return { ...defaultObj, value: 'home' }
//   return { ...defaultObj, value: 'public_profile' }
// })

const postId = computed(() => {
	if (props.post === null) return null
	return props.post.id
})

const isShowPrice = computed(() => {
	return (
		props.post &&
		props.isExplore &&
		!props.isCurrentModeDefault &&
		props.post.isExpressCheckout
	)
})

const isLocalCurrency = computed(() => {
	if (
		props.localCurrencyInfo &&
		props.localCurrencyInfo.length &&
		!props.isCurrentModeDefault
	) {
		const temp: any = props.localCurrencyInfo.filter(
			(item: any) => item.key === props.post.warehouse,
		)
		if (temp.length) {
			return temp[0].isLocal
		}
		return false
	}
	return false
})

const currencySymbol = computed(() => {
	if (!props.supportedCurrencies || !props.post.warehouse) return ''
	if (!props.warehouseIdMap?.[props.post.warehouse]) return null
	if (props.warehouseIdMap[props.post.warehouse].currency === undefined)
		return null
	const { currency } = props.warehouseIdMap[props.post.warehouse]
	return props.supportedCurrencies[currency.toLowerCase()].symbol
})

const localPricing = computed(() => {
	if (isLocalCurrency.value) {
		return getformatFloatPrice({
			price: props.post.ecLocalPrice,
		})
	}
	return ''
})

const priceOriginal = computed(() => {
	if (isShowPrice.value && props.post) {
		return `${currencySymbolOriginal.value} ${Number(
			props.post.ecPrice,
		).toLocaleString('en')}`
	}
	return ''
})

const isMemberOfficial1 = computed(() => {
	if (props.post?.user?.member_types?.length) {
		return props.post.user.member_types.includes(MemberTypes.Official1)
	}
	return false
})

const isMemberAssigned1 = computed(() => {
	if (props.post?.user?.member_types?.length) {
		return props.post.user.member_types.includes(MemberTypes.Assigned1)
	}
	return false
})

const isMemberAssigned2 = computed(() => {
	if (props.post?.user?.member_types?.length) {
		return props.post.user.member_types.includes(MemberTypes.Assigned2)
	}
	return false
})

function viewPost() {
	emit('view-post')
	createGAEvent('ps_active_user')
}

function updatePostInfo(postInfo: any) {
	createGAEvent('ps_active_user')
	// need to fixed, can not modify props directly
	props.post.likeCount = postInfo.likeCount
	props.post.isLiked = postInfo.isLiked
	emit('like', {
		id: postId.value,
		param: 'isLiked',
		val: postInfo.isLiked,
		likeCount: postInfo.likeCount,
		slug: props.post.slug,
	})
}

function getMedia(mediaObj: any) {
	if (!mediaObj || !Object.keys(mediaObj).length || !mediaObj.content)
		return null
	if (mediaObj.type === 'ps_video') return mediaObj.preview
	if (!mediaObj.has_resize) return mediaObj.content
	return mediaObj[ProductSharingImageSize.PostList]
}

function getPosterImage(userImageObj: any) {
	if (
		!userImageObj ||
		!Object.keys(userImageObj).length ||
		!userImageObj.content
	)
		return null
	if (!userImageObj.has_resize) return userImageObj.content
	return userImageObj[ProductSharingImageSize.Profile]
}

function initObserver() {
	const oberverRoot = document.querySelector(
		'.bs-product-sharing-post-list__content',
	)
	const options = {
		oberverRoot,
		threshold: [0, 0.25, 0.5, 0.75, 1],
	}
	const callback = (entries: IntersectionObserverEntry[]) => {
		entries.forEach((entry: any) => {
			if (
				entry.isIntersecting &&
				!entry.target.dataset.isSendEvent &&
				entry.target.dataset.postId
			) {
				// createGAEvent('ps_post_viewed', eventParams.value)
				entry.target.dataset.isSendEvent = 'true'
			}
		})
	}
	const observer = new IntersectionObserver(callback, options)
	const loadingObserver = postItem.value!
	observer.observe(loadingObserver)
}

function likePost() {
	likeIconCompoent.value?.like()
}

async function setCurrencySymbolOriginal() {
	currencySymbolOriginal.value = getCurrencySymbolOriginal({
		warehouse: props.post.warehouse,
		warehouseIdMap: props.warehouseIdMap,
		supportedCurrencies: props.supportedCurrencies,
	})
}

watch(
	() => props.post,
	val => {
		if (val !== null && Object.keys(props.post).length) {
			initObserver()
			if (props.isExplore && !props.isCurrentModeDefault) {
				setCurrencySymbolOriginal()
			}
		}
	},
)
</script>

<template>
	<div
		ref="postItem"
		:class="[
			'grid-item bs-product-sharing-post-list-item',
			'relative float-left mb-[16px] bg-transparent py-0',
			isSearch || isExplore ? 'px-[8px] mdp:px-[10px]' : 'px-[6.5px]',
		]"
		:data-post-id="postId"
		data-is-send-event
	>
		<bs-normal-card
			:is-hovered-enabled="true"
			@content-click="viewPost"
			class="group !h-auto cursor-pointer !shadow-none"
		>
			<template #content>
				<template v-if="isLoading">
					<div class="h-[200px] w-full overflow-hidden rounded-[4px]">
						<div class="skeleton skeleton-line h-[200px]"></div>
					</div>
				</template>
				<template v-else>
					<div
						v-if="postItemInfo.isExpressCheckout"
						class="absolute left-[8px] top-[8px] z-[1] flex h-[24px] w-[24px] items-center justify-center"
					>
						<span
							class="bs-icon inline-block h-[24px] w-[24px] bg-[length:100%_100%]"
							style="
								background-image: url('data:image/svg+xml;base64,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');
							"
						></span>
					</div>
					<div
						v-if="postSymbols"
						class="absolute right-[8px] top-[8px] z-[1] flex h-[24px] w-[24px] items-center justify-center"
					>
						<div
							class="absolute h-full w-full rounded-full bg-black opacity-10"
						></div>
						<span
							v-if="postSymbols === 'video'"
							class="bs-icon inline-block h-[14px] w-[14px] bg-[length:100%_100%]"
							style="
								background-image: url('data:image/svg+xml;charset=UTF-8,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%0A%3Csvg%20%20fill%3D%22%23fff%22%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%0A%20%20%20%20%3Cg%20id%3D%22Symbols%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill-rule%3D%22evenodd%22%3E%0A%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M10%2C20%20C8.61666667%2C20%207.31666667%2C19.7373333%206.1%2C19.212%20C4.88333333%2C18.6873333%203.825%2C17.975%202.925%2C17.075%20C2.025%2C16.175%201.31266667%2C15.1166667%200.788%2C13.9%20C0.262666667%2C12.6833333%200%2C11.3833333%200%2C10%20C0%2C8.61666667%200.262666667%2C7.31666667%200.788%2C6.1%20C1.31266667%2C4.88333333%202.025%2C3.825%202.925%2C2.925%20C3.825%2C2.025%204.88333333%2C1.31233333%206.1%2C0.787%20C7.31666667%2C0.262333333%208.61666667%2C0%2010%2C0%20C11.3833333%2C0%2012.6833333%2C0.262333333%2013.9%2C0.787%20C15.1166667%2C1.31233333%2016.175%2C2.025%2017.075%2C2.925%20C17.975%2C3.825%2018.6873333%2C4.88333333%2019.212%2C6.1%20C19.7373333%2C7.31666667%2020%2C8.61666667%2020%2C10%20C20%2C11.3833333%2019.7373333%2C12.6833333%2019.212%2C13.9%20C18.6873333%2C15.1166667%2017.975%2C16.175%2017.075%2C17.075%20C16.175%2C17.975%2015.1166667%2C18.6873333%2013.9%2C19.212%20C12.6833333%2C19.7373333%2011.3833333%2C20%2010%2C20%20Z%20M7.5%2C14.5%20L14.5%2C10%20L7.5%2C5.5%20L7.5%2C14.5%20Z%22%20id%3D%22play_circle_FILL1_wght400_GRAD0_opsz24%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%0A%20%20%20%20%3C%2Fg%3E%0A%3C%2Fsvg%3E%0A');
							"
						></span>
						<span
							v-else-if="postSymbols === 'images'"
							class="bs-icon inline-block h-[14px] w-[14px] bg-[length:100%_100%]"
							style="
								background-image: url('data:image/svg+xml;charset=UTF-8,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%0A%3Csvg%20%20fill%3D%22%23fff%22%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%0A%20%20%20%20%3Cg%20id%3D%22Symbols%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill-rule%3D%22evenodd%22%3E%0A%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M4.5%2C17%20C4.1%2C17%203.75%2C16.85%203.45%2C16.55%20C3.15%2C16.25%203%2C15.9%203%2C15.5%20L3%2C1.5%20C3%2C1.1%203.15%2C0.75%203.45%2C0.45%20C3.75%2C0.15%204.1%2C0%204.5%2C0%20L18.5%2C0%20C18.9%2C0%2019.25%2C0.15%2019.55%2C0.45%20C19.85%2C0.75%2020%2C1.1%2020%2C1.5%20L20%2C15.5%20C20%2C15.9%2019.85%2C16.25%2019.55%2C16.55%20C19.25%2C16.85%2018.9%2C17%2018.5%2C17%20L4.5%2C17%20Z%20M1.5%2C20%20C1.1%2C20%200.75%2C19.85%200.45%2C19.55%20C0.15%2C19.25%200%2C18.9%200%2C18.5%20L0%2C3%20L1.5%2C3%20L1.5%2C18.5%20C1.5%2C18.5%201.5%2C18.5%201.5%2C18.5%20C1.5%2C18.5%201.5%2C18.5%201.5%2C18.5%20L17%2C18.5%20L17%2C20%20L1.5%2C20%20Z%22%20id%3D%22multi_FILL1_wght400_GRAD0_opsz48%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%0A%20%20%20%20%3C%2Fg%3E%0A%3C%2Fsvg%3E%0A');
							"
						></span>
					</div>
					<div class="relative overflow-hidden rounded-[8px]">
						<img
							class="block w-full bg-transparent object-cover transition-transform duration-300 can-hover:group-hover:scale-105"
							:style="{ height: `${calculateImageHeight}px` }"
							:src="postItemImageSrc"
							alt="post"
							loading="lazy"
						/>
					</div>
				</template>
				<div
					class="flex flex-col px-8px py-12px"
					:class="{ 'bg-o-50': isBgOrange }"
				>
					<div class="mb-[8px] flex items-center">
						<div class="mr-[8px] grid h-[20px] w-[20px] place-content-center">
							<template v-if="!isLoading">
								<img
									v-if="postItemInfo && postItemInfo.user.image"
									class="h-[20px] w-[20px] rounded-full object-cover"
									:src="getPosterImage(postItemInfo.user.image)"
									alt="Avatar"
									loading="lazy"
								/>
								<img
									v-else
									class="h-[20px] w-[20px] rounded-full object-cover"
									:src="getVueAssetImagePath('avatar_2_48px@2x.png')"
									alt="Avatar"
									loading="lazy"
								/>
							</template>
							<template v-else>
								<div
									class="skeleton skeleton-line h-[20px] w-[20px] rounded-full"
								></div>
							</template>
						</div>
						<div class="max-w-[calc(100%-20px)] items-center">
							<div
								class="flex items-center text-[0.75rem] font-normal leading-[1rem] tracking-[0.35px] text-n-500 lg:text-[0.75rem] lg:leading-[1rem]"
							>
								<template v-if="!isLoading">
									<span
										class="inline-block overflow-hidden text-ellipsis whitespace-nowrap"
									>
										{{ postItemInfo.user.username }}
									</span>
									<BaseIcon
										class="bs-icon-member-official-1"
										v-if="isMemberOfficial1"
										name="badge"
									/>
									<span
										class="ml-[8px] inline-block h-[16px] w-[16px] flex-shrink-0 bg-[length:100%_100%]"
										style="
											background-image: url('data:image/svg+xml;charset=UTF-8,%3Csvg%20%20fill%3D%22%23FF9E1B%22%20width%3D%2217%22%20height%3D%2217%22%20viewBox%3D%220%200%2017%2017%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M14.8327%206.66602C14.3403%206.66602%2013.9404%207.06587%2013.9404%207.5583C13.9404%207.70992%2013.9793%207.85349%2014.0451%207.97827L11.324%209.4569C11.324%209.4569%209.72057%206.38559%209.1369%205.26656C9.42135%205.11762%209.61591%204.81975%209.61591%204.47626C9.61591%203.98383%209.21606%203.58398%208.72363%203.58398C8.2312%203.58398%207.83136%203.98383%207.83136%204.47626C7.83136%204.81975%208.02591%205.11762%208.31037%205.26656L6.12329%209.4569L3.40218%207.97827C3.46927%207.85349%203.50684%207.70992%203.50684%207.5583C3.50684%207.06587%203.10699%206.66602%202.61457%206.66602C2.12214%206.66602%201.72363%207.06587%201.72363%207.5583C1.72363%208.05073%202.12348%208.45057%202.61591%208.45057C2.69105%208.45057%202.76484%208.43984%202.83462%208.4224L4.19785%2013.8405H13.2494L14.6126%208.4224C14.6824%208.43984%2014.7562%208.45057%2014.8314%208.45057C15.3238%208.45057%2015.7236%208.05073%2015.7236%207.5583C15.7236%207.06587%2015.3238%206.66602%2014.8314%206.66602H14.8327Z%22%2F%3E%0A%3C%2Fsvg%3E%0A');
										"
										v-if="isMemberAssigned1"
									></span>
									<span
										class="ml-[8px] inline-block h-[16px] w-[16px] flex-shrink-0 bg-[length:100%_100%]"
										style="
											background-image: url('data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M10.6319%2012.2846L8.33705%2010.9597C8.19356%2010.8769%208.01008%2010.926%207.92724%2011.0695L3.38968%2018.9288C3.27802%2019.1222%203.40927%2019.3655%203.63222%2019.3783L6.29257%2019.5317C6.38709%2019.5371%206.4735%2019.5869%206.52566%2019.6659L8.00323%2021.9038C8.1261%2022.0899%208.40188%2022.0816%208.51339%2021.8885L13.0516%2014.0281C13.1344%2013.8846%2013.0853%2013.7011%2012.9418%2013.6183L10.6319%2012.2846Z%22%20fill%3D%22%231B4883%22%2F%3E%0A%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M13.3682%2012.2846L15.663%2010.9597C15.8065%2010.8769%2015.9899%2010.926%2016.0728%2011.0695L20.6103%2018.9288C20.722%2019.1222%2020.5908%2019.3655%2020.3678%2019.3783L17.7075%2019.5317C17.6129%2019.5371%2017.5265%2019.5869%2017.4744%2019.6659L15.9968%2021.9038C15.8739%2022.0899%2015.5982%2022.0816%2015.4866%2021.8885L10.9484%2014.0281C10.8656%2013.8846%2010.9148%2013.7011%2011.0583%2013.6183L13.3682%2012.2846Z%22%20fill%3D%22%231B4883%22%2F%3E%0A%3Ccircle%20cx%3D%2212%22%20cy%3D%2210.0845%22%20r%3D%228.39335%22%20fill%3D%22%23FF9E1B%22%2F%3E%0A%3Cpath%20d%3D%22M8.96256%2011.8936C8.98758%2011.7476%208.93918%2011.5987%208.83312%2011.4953L6.65775%209.37494C6.39065%209.1146%206.53803%208.66099%206.90714%208.60737L9.9136%208.17056C10.0602%208.14927%2010.1869%208.05721%2010.2524%207.92439L11.5968%205.20046C11.7618%204.866%2012.2388%204.866%2012.4038%205.20046L13.7482%207.9244C13.8137%208.05721%2013.9404%208.14927%2014.087%208.17057L17.0929%208.60735C17.462%208.66098%2017.6094%209.11458%2017.3423%209.37492L15.1669%2011.4953C15.0609%2011.5987%2015.0125%2011.7476%2015.0375%2011.8936L15.5512%2014.8874C15.6143%2015.2551%2015.2285%2015.5354%2014.8983%2015.3618L12.2097%2013.9483C12.0786%2013.8794%2011.922%2013.8794%2011.7909%2013.9483L9.10216%2015.3619C8.77204%2015.5355%208.3862%2015.2552%208.44923%2014.8876L8.96256%2011.8936Z%22%20fill%3D%22white%22%2F%3E%0A%3C%2Fsvg%3E%0A');
										"
										v-if="isMemberAssigned2"
									></span>
								</template>
								<template v-else>
									<div
										class="skeleton skeleton-line mb-[6px] mt-[5px] h-[10px] w-[70px]"
									></div>
								</template>
							</div>
						</div>
					</div>

					<span
						class="mb-[12px] text-[0.875rem] font-medium leading-[1.25rem] tracking-[0.35px] lg:text-[0.875rem] lg:leading-[1.25rem]"
					>
						<template v-if="!isLoading">{{ postItemInfo.title }}</template>
						<template v-else>
							<div class="skeleton skeleton-line h-[12px] w-[95%]"></div>
						</template>
					</span>

					<div
						class="text-[1.125rem] font-semibold leading-[1.5rem] tracking-[0.35px] text-n-800 lg:text-[1.125rem] lg:leading-[1.5rem]"
						v-if="isShowPrice"
					>
						<template v-if="!isLoading">
							{{ isLocalCurrency ? localPricing : priceOriginal }}
						</template>
						<template v-else>
							<div class="skeleton skeleton-line"></div>
						</template>
					</div>
					<div
						class="flex items-center gap-[4px]"
						:class="{ 'mt-[12px]': isShowPrice }"
					>
						<template v-if="!isLoading">
							<BsProductSharingLikeIcon
								class="bs-like-icon"
								ref="likeIconCompoent"
								:post-info="postItemInfo"
								:width="16"
								:height="16"
								:text="text"
								@update-post-info="updatePostInfo"
							/>
							<span
								v-if="totalLikeCountDisplay"
								class="text-[0.75rem] font-normal leading-[1rem] tracking-[0.35px] text-n-500 lg:text-[0.875rem] lg:leading-[1.25rem]"
							>
								{{ totalLikeCountDisplay }}
							</span>
						</template>
						<template v-else>
							<div class="skeleton skeleton-line h-[12px] w-[85%]"></div>
						</template>
					</div>
				</div>
			</template>
		</bs-normal-card>
	</div>
</template>
